import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col, Card, CardBody, Table, Badge, Input, ButtonGroup, Pagination, PaginationItem, PaginationLink, Label, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Button, Offcanvas, OffcanvasHeader, OffcanvasBody } from "reactstrap";
import { withTranslation } from "react-i18next";
import PageTitle from "../../components/Common/PageTitle";
import UserService from "Api/Services/UserService";
import debounce from 'lodash/debounce';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import DoctorSelector from "../../components/Common/DoctorSelector";
import {
  getStoredSelectedDoctorId,
  isGroupLoginUser,
  setStoredSelectedDoctorId,
} from "../../helpers/doctorSelection";

const parsePortalDate = (dateValue) => {
  const dateMatch = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return dateMatch
    ? new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]))
    : new Date(dateValue);
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Skeleton loading component
const SkeletonRow = () => (
  <tr>
    <td><div className="skeleton-loader" style={{ height: '20px', width: '120px' }}></div></td>
    <td><div className="skeleton-loader" style={{ height: '20px', width: '150px' }}></div></td>
    <td><div className="skeleton-loader" style={{ height: '20px', width: '100px' }}></div></td>
    <td><div className="skeleton-loader" style={{ height: '20px', width: '150px' }}></div></td>
    <td><div className="skeleton-loader" style={{ height: '20px', width: '200px' }}></div></td>
    <td><div className="skeleton-loader" style={{ height: '20px', width: '80px' }}></div></td>
  </tr>
);

const Appointments = (props) => {
  document.title = "Appointments | Infuse One";
  
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('appt_date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isSorting, setIsSorting] = useState(false);
  const [sortingColumn, setSortingColumn] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen(prevState => !prevState);

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState(getStoredSelectedDoctorId());
  const [groupLogin] = useState(isGroupLoginUser());

  const toggleDetails = () => setIsDetailsOpen(!isDetailsOpen);

  // Set default start and end dates for next 14 days
  const today = new Date();
  const todayStr = toDateInputValue(today);
  const plus14 = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
  const plus14Str = toDateInputValue(plus14);

  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(plus14Str);

  const loadAccessibleDoctors = useCallback(async () => {
    try {
      const response = await UserService.GetAccessibleDoctors().json();

      if (!response.status) {
        setError(response.message || "Failed to load doctors");
        return "";
      }

      const availableDoctors = response.data || [];
      const fallbackDoctorId = response.selected_doctor?.id ? String(response.selected_doctor.id) : "";
      const storedDoctorId = getStoredSelectedDoctorId();
      const nextDoctorId = availableDoctors.some(doctor => String(doctor.id) === storedDoctorId)
        ? storedDoctorId
        : fallbackDoctorId;

      setDoctors(availableDoctors);
      setSelectedDoctorId(nextDoctorId);
      setStoredSelectedDoctorId(nextDoctorId);

      return nextDoctorId;
    } catch (loadError) {
      setError("Failed to load doctors");
      return "";
    }
  }, []);

  const debouncedFetchAppointments = useCallback(
    debounce(async (params) => {
      try {
        setIsFetching(true);
        setLoading(true);
        
        const response = await UserService.getAppointments({
          page: params.page || 1,
          search: params.search?.trim(),
          start_date: params.start_date,
          end_date: params.end_date,
          status: params.status !== 'all' ? params.status : undefined,
          sort_field: params.sort_field || 'appt_date',
          sort_direction: params.sort_direction || 'asc',
          perPage: params.perPage || 20,
          doctorId: params.doctorId || undefined,
        });
        
        if (response.status && response.data) {
          setAppointments(response.data.appointments);
          setPagination(response.data.pagination);
          setHasMore(response.data.pagination.current_page < response.data.pagination.last_page);
          setError(null);
        } else {
          setError(response.error || 'Failed to fetch appointments');
          setAppointments([]);
        }
      } catch (err) {
        setError('Failed to fetch appointments');
        setAppointments([]);
      } finally {
        setIsFetching(false);
        setLoading(false);
        setIsSorting(false);
        setSortingColumn(null);
      }
    }, 150),
    []
  );

  // Initial load
  useEffect(() => {
    const initializeAppointments = async () => {
      if (groupLogin) {
        await loadAccessibleDoctors();
        return;
      }

      debouncedFetchAppointments({
        page: 1,
        search: searchTerm,
        start_date: startDate,
        end_date: endDate,
        status: statusFilter,
        sort_field: sortField,
        sort_direction: sortDirection,
        perPage: 20
      });
    };

    initializeAppointments();
  }, [debouncedFetchAppointments, groupLogin, loadAccessibleDoctors]);

  useEffect(() => {
    if (!groupLogin || !selectedDoctorId) {
      return;
    }

    setStoredSelectedDoctorId(selectedDoctorId);
    UserService.clearAppointmentsCache();

    debouncedFetchAppointments({
      page: 1,
      search: searchTerm,
      start_date: startDate,
      end_date: endDate,
      status: statusFilter,
      sort_field: sortField,
      sort_direction: sortDirection,
      perPage: 20,
      doctorId: selectedDoctorId,
    });
  }, [debouncedFetchAppointments, groupLogin, selectedDoctorId]);

  const handleSearch = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    
    // Clear cache when search term changes
    UserService.clearAppointmentsCache();
    
    debouncedFetchAppointments({
      page: 1,
      search: value,
      start_date: startDate,
      end_date: endDate,
      status: statusFilter,
      sort_field: sortField,
      sort_direction: sortDirection,
      perPage: 20,
      doctorId: groupLogin ? selectedDoctorId : undefined,
    });
  }, [endDate, groupLogin, selectedDoctorId, sortField, sortDirection, startDate, statusFilter]);

  const handleStartDateChange = (e) => {
    const value = e.target.value;
    setStartDate(value);
    setCurrentPage(1);
    
    // Ensure end date is not before start date
    if (endDate && value > endDate) {
      setEndDate(value);
    }
    
    // Clear cache when date changes
    UserService.clearAppointmentsCache();
    
    debouncedFetchAppointments({
      page: 1,
      search: searchTerm,
      start_date: value,
      end_date: endDate,
      status: statusFilter,
      sort_field: sortField,
      sort_direction: sortDirection,
      perPage: 20,
      doctorId: groupLogin ? selectedDoctorId : undefined,
    });
  };
  
  const handleEndDateChange = (e) => {
    const value = e.target.value;
    setEndDate(value);
    setCurrentPage(1);
    
    // Ensure start date is not after end date
    if (startDate && value < startDate) {
      setStartDate(value);
    }
    
    // Clear cache when date changes
    UserService.clearAppointmentsCache();
    
    debouncedFetchAppointments({
      page: 1,
      search: searchTerm,
      start_date: startDate,
      end_date: value,
      status: statusFilter,
      sort_field: sortField,
      sort_direction: sortDirection,
      perPage: 20,
      doctorId: groupLogin ? selectedDoctorId : undefined,
    });
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    setAppointments([]); // Clear current appointments when filtering
    
    // Clear cache when status changes
    UserService.clearAppointmentsCache();
    
    debouncedFetchAppointments({
      page: 1,
      search: searchTerm,
      start_date: startDate,
      end_date: endDate,
      status: status === 'all' ? '' : status,
      sort_field: sortField,
      sort_direction: sortDirection,
      perPage: 20,
      doctorId: groupLogin ? selectedDoctorId : undefined,
    });
  };

  const handleSort = useCallback(async (field) => {
    setSortingColumn(field);
    setIsSorting(true);

    const newDirection = sortField === field ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortField(field);
    setSortDirection(newDirection);

    setCurrentPage(1);
    setAppointments([]); // Clear current appointments when sorting
    
    // Clear cache when sorting changes
    UserService.clearAppointmentsCache();
    
    debouncedFetchAppointments({
      page: 1,
      search: searchTerm,
      start_date: startDate,
      end_date: endDate,
      status: statusFilter,
      sort_field: field,
      sort_direction: newDirection,
      perPage: 20,
      doctorId: groupLogin ? selectedDoctorId : undefined,
    });
  }, [endDate, groupLogin, searchTerm, selectedDoctorId, sortField, sortDirection, startDate, statusFilter]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    debouncedFetchAppointments({
      page,
      search: searchTerm,
      start_date: startDate,
      end_date: endDate,
      status: statusFilter,
      sort_field: sortField,
      sort_direction: sortDirection,
      perPage: 20,
      doctorId: groupLogin ? selectedDoctorId : undefined,
    });
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "scheduled": return "warning";
      case "complete":
      case "completed": return "success";
      case "canceled":
      case "cancelled": return "danger";
      default: return "secondary";
    }
  };

  const getSortIcon = (field) => {
    if (sortingColumn === field) {
      return <i className="bx bx-loader-alt bx-spin"></i>;
    }
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const currentPage = pagination.current_page;
    const totalPages = pagination.last_page;

    // Always show first page
    pages.push(
      <PaginationItem key={1} active={currentPage === 1}>
        <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
      </PaginationItem>
    );

    let startPage = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 2);

    if (startPage > 2) {
      pages.push(
        <PaginationItem key="start-ellipsis" disabled>
          <PaginationLink>...</PaginationLink>
        </PaginationItem>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i} active={currentPage === i}>
          <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
        </PaginationItem>
      );
    }

    if (endPage < totalPages - 1) {
      pages.push(
        <PaginationItem key="end-ellipsis" disabled>
          <PaginationLink>...</PaginationLink>
        </PaginationItem>
      );
    }

    if (totalPages > 1) {
      pages.push(
        <PaginationItem key={totalPages} active={currentPage === totalPages}>
          <PaginationLink onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return (
      <Pagination className="pagination pagination-rounded justify-content-center mt-4">
        <PaginationItem disabled={currentPage === 1}>
          <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
        </PaginationItem>
        {pages}
        <PaginationItem disabled={currentPage === totalPages}>
          <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
        </PaginationItem>
      </Pagination>
    );
  };

  const handleAppointmentSelect = (appointment) => {
    setSelectedAppointment(appointment);
    setIsDetailsOpen(true);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="container-fluid user-page-shell">
            <PageTitle
              title="Appointments"
              actions={groupLogin ? (
                <DoctorSelector
                  doctors={doctors}
                  selectedDoctorId={selectedDoctorId}
                  onChange={setSelectedDoctorId}
                  compact
                />
              ) : null}
            />
            <div className="user-page-summary">
              {(() => {
                const formatDate = (dateStr) => {
                  const date = parsePortalDate(dateStr);
                  return date.toLocaleDateString('en-US', { 
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  });
                };
                return `Dates: ${formatDate(startDate)} - ${formatDate(endDate)}  |  Appointment Status: ${
                  statusFilter === 'all' ? 'All' : 
                  statusFilter === 'Active' ? 'Active' :
                  statusFilter === 'Complete' ? 'Complete' :
                  statusFilter === 'Canceled' ? 'Canceled' : 'All'
                }`;
              })()}
            </div>
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
            <Row>
              <Col>
                <Card>
                  <CardBody>
                    <Row className="mb-4 align-items-end">
                      <Col md="4">
                        <div className="d-flex flex-column">
                          <Label className="mb-1">Search</Label>
                          <div className="position-relative">
                            <i className="bx bx-search-alt-2 position-absolute" style={{ 
                              left: '12px', 
                              top: '50%', 
                              transform: 'translateY(-50%)',
                              zIndex: 1 
                            }}></i>
                            <Input
                              type="text"
                              className="form-control"
                              placeholder="Patient name"
                              value={searchTerm}
                              onChange={handleSearch}
                              style={{ 
                                paddingLeft: '35px',
                                height: '38px',
                                borderRadius: '4px'
                              }}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col md="5">
                        <div className="d-flex gap-3">
                          <div className="flex-grow-1">
                            <div className="d-flex flex-column">
                              <Label className="mb-1">From</Label>
                              <Input
                                type="date"
                                className="form-control"
                                value={startDate}
                                onChange={handleStartDateChange}
                                placeholder="yyyy-mm-dd"
                              />
                            </div>
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex flex-column">
                              <Label className="mb-1">To</Label>
                              <Input
                                type="date"
                                className="form-control"
                                value={endDate}
                                onChange={handleEndDateChange}
                                placeholder="yyyy-mm-dd"
                              />
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col md="3">
                        <div className="d-flex flex-column">
                          <Label className="mb-1">Status</Label>
                          <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} className="w-100">
                            <DropdownToggle caret className="w-100 text-start bg-white border text-dark d-flex justify-content-between align-items-center status-dropdown-toggle" style={{ borderColor: '#CDD3D9', borderWidth: '1.5px', borderStyle: 'solid', boxShadow: 'none', background: '#fff' }}>
                              <span className="text-dark">
                                {statusFilter === 'all' ? 'All' : 
                                 statusFilter === 'Active' ? 'Active' :
                                 statusFilter === 'Complete' ? 'Complete' :
                                 statusFilter === 'Canceled' ? 'Canceled' : 'All'}
                              </span>
                              <i className="bx bx-chevron-down text-dark"></i>
                            </DropdownToggle>
                            <DropdownMenu className="w-100">
                              <DropdownItem active={statusFilter === 'all'} onClick={() => handleStatusFilter('all')} className="text-dark">All</DropdownItem>
                              <DropdownItem active={statusFilter === 'Active'} onClick={() => handleStatusFilter('Active')} className="text-dark">Active</DropdownItem>
                              <DropdownItem active={statusFilter === 'Complete'} onClick={() => handleStatusFilter('Complete')} className="text-dark">Complete</DropdownItem>
                              <DropdownItem active={statusFilter === 'Canceled'} onClick={() => handleStatusFilter('Canceled')} className="text-dark">Canceled</DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive">
                      <Table className="table-centered table-nowrap mb-0">
                        <thead>
                          <tr>
                            <th onClick={() => handleSort('patient_name')} style={{ cursor: 'pointer', padding: '1rem 0.75rem' }}>
                              Patient Name {getSortIcon('patient_name')}
                            </th>
                            <th onClick={() => handleSort('appt_date')} style={{ cursor: 'pointer', padding: '1rem 0.75rem' }}>
                              Appointment Date {getSortIcon('appt_date')}
                            </th>
                            <th onClick={() => handleSort('location_name')} style={{ cursor: 'pointer', padding: '1rem 0.75rem' }}>
                              Location {getSortIcon('location_name')}
                            </th>
                            <th onClick={() => handleSort('prescription')} style={{ cursor: 'pointer', padding: '1rem 0.75rem', width: '220px' }}>
                              Prescription {getSortIcon('prescription')}
                            </th>
                            <th style={{ padding: '1rem 0.75rem', paddingRight: '2rem' }}>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <>
                              <SkeletonRow />
                              <SkeletonRow />
                              <SkeletonRow />
                            </>
                          ) : appointments.length > 0 ? (
                            appointments.map((appointment, index) => (
                              <tr key={appointment.id || index}>
                                <td style={{ padding: '1rem 0.75rem', verticalAlign: 'middle' }}>{appointment.patient_name || 'N/A'}</td>
                                <td style={{ padding: '1rem 0.75rem', verticalAlign: 'middle' }}>{appointment.appt_date ? (() => {
                                  const date = parsePortalDate(appointment.appt_date);
                                  const weekday = date.toLocaleString(undefined, { weekday: 'short' });
                                  const month = date.toLocaleString(undefined, { month: 'long' });
                                  const day = date.getDate();
                                  const year = date.getFullYear();
                                  return `${weekday}. ${month} ${day}, ${year}`;
                                })() : 'N/A'}</td>
                                <td style={{ padding: '1rem 0.75rem', verticalAlign: 'middle' }}>{appointment.location_name || 'N/A'}</td>
                                <td style={{ padding: '1rem 0.75rem', maxWidth: '220px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' }}>{appointment.prescription || 'N/A'}</td>
                                <td style={{ padding: '1rem 0.75rem', paddingRight: '2rem', verticalAlign: 'middle' }}>
                                  <Badge
                                    color={
                                      appointment.appt_status?.toLowerCase() === "active" ? undefined :
                                      appointment.appt_status?.toLowerCase() === "complete" || appointment.appt_status?.toLowerCase() === "completed" ? undefined :
                                      appointment.appt_status?.toLowerCase() === "scheduled" ? "warning" :
                                      appointment.appt_status?.toLowerCase() === "canceled" || appointment.appt_status?.toLowerCase() === "cancelled" ? "danger" :
                                      "secondary"
                                    }
                                    className={`status-badge${appointment.appt_status?.toLowerCase() === "active" ? ' status-badge-active' : ''}${(appointment.appt_status?.toLowerCase() === "complete" || appointment.appt_status?.toLowerCase() === "completed") ? ' status-badge-complete' : ''}`}
                                  >
                                    {appointment.appt_status ? appointment.appt_status.charAt(0).toUpperCase() + appointment.appt_status.slice(1).toLowerCase() : 'Unknown'}
                                  </Badge>
                                </td>
                                <td style={{ padding: '1rem 0.75rem', verticalAlign: 'middle' }}>
                                  <Button
                                    className="btn-primary"
                                    size="sm"
                                    onClick={() => handleAppointmentSelect(appointment)}
                                  >
                                    View Details
                                  </Button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center" style={{ padding: '1rem 0.75rem', verticalAlign: 'middle' }}>
                                No appointments found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {!loading && !error && renderPagination()}
                    
                    {isFetching && (
                      <div className="text-center mt-3">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </div>

      {/* Details Sidebar */}
      <Offcanvas
        isOpen={isDetailsOpen}
        toggle={toggleDetails}
        direction="end"
        className="appointment-details-sidebar"
        style={{ width: '600px' }}
      >
        <OffcanvasHeader toggle={toggleDetails} style={{ padding: '2.5rem 2rem 1.5rem 2rem' }}>
          <h5 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: '500' }}>Appointment Details</h5>
        </OffcanvasHeader>
        <OffcanvasBody style={{ padding: '0 1.5rem' }}>
          {selectedAppointment && (
            <div className="appointment-details">
              <Card className="mb-3" style={{ marginTop: '0.5rem' }}>
                <CardBody style={{ paddingTop: '1.25rem' }}>
                  <h3 className="card-title mb-2" style={{ fontSize: '1.35rem', fontWeight: '600' }}>
                    {selectedAppointment.patient_name || 'N/A'}
                  </h3>
                  <div className="mt-2">
                    <Badge
                      color={
                        selectedAppointment.appt_status?.toLowerCase() === "active" ? undefined :
                        selectedAppointment.appt_status?.toLowerCase() === "complete" || selectedAppointment.appt_status?.toLowerCase() === "completed" ? undefined :
                        selectedAppointment.appt_status?.toLowerCase() === "scheduled" ? "warning" :
                        selectedAppointment.appt_status?.toLowerCase() === "canceled" || selectedAppointment.appt_status?.toLowerCase() === "cancelled" ? "danger" :
                        "secondary"
                      }
                      className={`status-badge${selectedAppointment.appt_status?.toLowerCase() === "active" ? ' status-badge-active' : ''}${(selectedAppointment.appt_status?.toLowerCase() === "complete" || selectedAppointment.appt_status?.toLowerCase() === "completed") ? ' status-badge-complete' : ''}`}
                      style={{ 
                        fontSize: '0.9rem',
                        padding: '0.4rem 0.8rem',
                        fontWeight: '500'
                      }}
                    >
                      {selectedAppointment.appt_status ? selectedAppointment.appt_status.charAt(0).toUpperCase() + selectedAppointment.appt_status.slice(1).toLowerCase() : 'Unknown'}
                    </Badge>
                  </div>
                </CardBody>
              </Card>

              <Card className="mb-3">
                <CardBody>
                  <h6 className="text-muted mb-3" style={{ fontSize: '1rem' }}>Appointment Information</h6>
                  <div className="d-flex mb-2">
                    <div className="text-muted" style={{width: '40%', fontSize: '0.95rem'}}>Date:</div>
                    <div style={{width: '60%', fontSize: '0.95rem'}}>
                      {selectedAppointment.appt_date ? (() => {
                        const date = parsePortalDate(selectedAppointment.appt_date);
                        const weekday = date.toLocaleString(undefined, { weekday: 'short' });
                        const month = date.toLocaleString(undefined, { month: 'long' });
                        const day = date.getDate();
                        const year = date.getFullYear();
                        return `${weekday}. ${month} ${day}, ${year}`;
                      })() : 'N/A'}
                    </div>
                  </div>
                  <div className="d-flex mb-2">
                    <div className="text-muted" style={{width: '40%', fontSize: '0.95rem'}}>Location:</div>
                    <div style={{width: '60%', fontSize: '0.95rem'}}>{selectedAppointment.location_name || 'N/A'}</div>
                  </div>
                </CardBody>
              </Card>

              <Card className="mb-3">
                <CardBody>
                  <h6 className="text-muted mb-3" style={{ fontSize: '1rem' }}>Treatment Information</h6>
                  <div className="d-flex mb-2">
                    <div className="text-muted" style={{width: '40%', fontSize: '0.95rem'}}>Prescription:</div>
                    <div style={{width: '60%', fontSize: '0.95rem'}}>{selectedAppointment.prescription || 'N/A'}</div>
                  </div>
                  <div className="d-flex mb-2">
                    <div className="text-muted" style={{width: '40%', fontSize: '0.95rem'}}>ICD Description:</div>
                    <div style={{width: '60%', fontSize: '0.95rem'}}>{selectedAppointment.icd_description || 'N/A'}</div>
                  </div>
                </CardBody>
              </Card>
            </div>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <style>
        {`
          .status-badge-active {
            background-color: #4BA3FA !important;
            color: #fff !important;
            border-color: #4BA3FA !important;
          }
          .status-badge-complete {
            background-color: #2ECC8B !important;
            color: #fff !important;
            border-color: #2ECC8B !important;
          }
          .status-dropdown-toggle {
            border-color: #CDD3D9 !important;
            border-width: 1.5px !important;
            background: #fff !important;
            box-shadow: none !important;
          }
          .icd-description-small {
            font-size: 0.92em;
            color: #444;
          }
          .offcanvas-header .btn-close {
            font-size: 1.25rem !important;
            color: #74788d !important;
            padding: 0.5rem !important;
            margin: -0.5rem -0.5rem -0.5rem auto !important;
          }
        `}
      </style>
    </React.Fragment>
  );
};

export default withTranslation()(Appointments); 
